<script setup lang="ts">
import type { Article } from '~/types'

interface Props {
  article: Article.Model
}

const props = defineProps<Props>()

const { title, shortDescription, articleDate, authorFullName, mainImage, showAuthor } = useArticle(props.article)
</script>

<template>
  <section class="article-intro">
    <Wrapper>
      <div class="article-intro__heading">
        <UIDynamicHeading
          v-if="title"
          level="1"
          as="h1"
          class="article-intro__title"
        >
          {{ title }}
        </UIDynamicHeading>
        <div class="article-intro__text">
          <p
            v-if="shortDescription"
            class="article-intro__caption"
          >
            {{ shortDescription }}
          </p>
          <div class="article-intro__meta">
            <time
              v-if="articleDate"
              class="article-intro__date"
            >
              {{ articleDate }}
            </time>
            <div
              v-if="showAuthor && authorFullName"
              class="article-intro__author"
            >
              <span>by <strong class="article-intro__author-name">{{ authorFullName }}</strong></span>
            </div>
            <div class="article-intro__share">
              <!-- TODO: add share -->
              <!-- <UIShare class="article-hero__share" /> -->
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="mainImage"
        class="article-intro__image-wrapper"
      >
        <UIImage
          :image="mainImage"
          class="article-intro__image"
        />
      </div>
    </Wrapper>
  </section>
</template>

<style scoped lang="scss">
.article-intro {
  @apply mb-[40px];
  @apply md:mb-[60px];
  @apply xl:mb-[90px];

  &__heading {
    @apply pt-[40px] pb-[30px] flex items-start justify-between flex-col bg-white;
    @apply md:pt-[80px] md:pb-[50px];
    @apply lg:flex-row;
    @apply xl:pt-[150px] xl:pb-[100px];
  }

  &:not(:has(&__image-wrapper)) &__heading {
    @apply pb-0;
  }

  &__title {
    @apply w-full mb-[30px] text-primary;
    @apply lg:w-1/2 lg:mb-0;
    @apply max-md:text-[28px];
  }

  &__text {
    @apply w-full;
    @apply lg:w-[420px];
  }

  &__caption {
    @apply text-[16px] leading-[1.4] text-primary;
    @apply md:text-[20px];
  }

  &__meta {
    @apply flex items-start justify-between flex-wrap gap-[20px] mt-[25px] text-[14px] font-semibold text-primary;
    @apply md:text-[16px] md:gap-[30px] md:mt-[35px];
  }

  &__author {
    @apply text-primary/70 max-w-[150px];

    &-name {
      @apply text-primary;
    }
  }

  &__image-wrapper {
    @apply -mx-[20px] w-[calc(100%+40px)] overflow-hidden;
    @apply md:mx-0 md:w-full md:rounded-[10px];
  }

  &__image {
    @apply w-full h-auto block;
  }
}
</style>
